import { useState } from 'react';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Button, IconButton } from '@/components/Button';
import { ViewSidebar } from '@/components/ViewSidebar';
import { InventoryViewPopover } from './InventoryViewPopover';
import { Empty, ErrorState, Skeleton } from '@/components/States';
import { money, qty } from '@/lib/format';
import { Applied, Chips, DataTable, Denied, FilterButton, k, Pager, PhoneBar, SearchBox, Sentinel, ViewToggle, type ChipItem, type Col, type ViewMode } from './ListKit';
import { FilterSheet } from './FilterSheet';
import { Avail, StockCard, warehouseNames } from './StockCards';
import { ProductStockSheet } from './ProductStockSheet';
import { InventoryAdjustDialog } from './InventoryAdjustDialog';
import { InventoryInboundDialog } from './InventoryInboundDialog';
import { InitialStockImportDialog } from './InitialStockImportDialog';
import { InventoryOutboundDialog } from './InventoryOutboundDialog';
import { InventoryActionsSheet } from './InventoryActionsSheet';
import { ShortagePage } from './ShortagePage';
import { useCategories, useInboundSuppliers, usePagedList, useUrlState, useWarehouses } from './api';
import { stockViews } from './status';
import type { StockStatusKey, StockSummaryRow, StockSummaryStats } from './types';

const FILTER_KEYS = ['category', 'warehouseId'] as const;

/**
 * 库存总览（老系统 /inventory 的「按产品」汇总）。
 * - 手机：卡片（默认），可切表格；无限滚动。
 * - iPad：表格（默认），可切卡片网格；分页。
 * - 桌面：表格 + 分页。
 * 视图 = 库存状态（接口 status 参数）；分类 / 仓库进「筛选」；点产品看分仓库存。
 */
export function InventoryPage() {
  const ff = useFormFactor();
  const can = useSession((st) => st.can);
  const allowed = can('inventory.read');
  const [sp, set] = useUrlState();
  const view = (sp.get('view') as StockStatusKey) || 'all';
  const q = sp.get('q') || '';
  const category = sp.get('category') || undefined;
  const warehouseId = sp.get('warehouseId') || undefined;
  const page = Number(sp.get('page')) || 1;
  const sort = sp.get('sort') || '';
  const dir = sp.get('dir') === 'asc' ? 'asc' : 'desc';
  const pid = sp.get('pid') || undefined;
  const [preferredPhoneMode, setPreferredPhoneMode] = useState<ViewMode>(() => {
    try { return localStorage.getItem('v2:inventory:phone-mode') === 'table' ? 'table' : 'cards'; }
    catch { return 'cards'; }
  });
  const mode: ViewMode = ff === 'phone' ? ((sp.get('mode') as ViewMode) || preferredPhoneMode) : 'table';
  const phone = ff === 'phone';
  const pageSize = phone ? 20 : 50;
  const [panel, setPanel] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(() => sp.get('action') === 'adjust' && import.meta.env.VITE_READONLY === '0' && can('inventory.adjust.write'));
  const [inboundOpen, setInboundOpen] = useState(() => sp.get('action') === 'inbound' && import.meta.env.VITE_READONLY === '0' && can('inventory.in.write'));
  const [importOpen, setImportOpen] = useState(() => sp.get('action') === 'import' && import.meta.env.VITE_READONLY === '0' && can('inventory.import.initial_stock'));
  const [outboundOpen, setOutboundOpen] = useState(() => sp.get('action') === 'outbound' && import.meta.env.VITE_READONLY === '0' && can('inventory.out.write'));
  const [actionsOpen, setActionsOpen] = useState(false);
  const [searching, setSearching] = useState(!!q);

  const list = usePagedList<StockSummaryRow, StockSummaryStats>(
    allowed && view !== 'shortage' ? '/api/v2/inventory/summary' : null,
    { mode: 'stock', status: view === 'all' ? undefined : view, keyword: q || undefined, category, warehouseId, sort: sort || undefined, dir: sort ? dir : undefined },
    { pageSize, append: phone, page },
  );
  const warehouses = useWarehouses(allowed);
  const inboundSuppliers = useInboundSuppliers(allowed && can('inventory.in.write'));
  const categories = useCategories(allowed);

  if (!allowed) return <Denied what="查看库存" perm="查看库存总览" />;
  if (view === 'shortage') return <ShortagePage />;

  const stats = list.stats;
  const showMoney = list.items.some((r) => r.inventoryValue !== undefined) || stats?.inventoryValue !== undefined;
  const canAdjust = import.meta.env.VITE_READONLY === '0' && can('inventory.adjust.write');
  const canInbound = import.meta.env.VITE_READONLY === '0' && can('inventory.in.write');
  const canImport = import.meta.env.VITE_READONLY === '0' && can('inventory.import.initial_stock');
  const canOutbound = import.meta.env.VITE_READONLY === '0' && can('inventory.out.write');
  const adjustDialog = adjustOpen && <InventoryAdjustDialog warehouses={warehouses} onClose={() => setAdjustOpen(false)} onSuccess={list.reload} />;
  const inboundDialog = inboundOpen && <InventoryInboundDialog warehouses={warehouses} suppliers={inboundSuppliers} onClose={() => setInboundOpen(false)} onSuccess={list.reload} />;
  const importDialog = importOpen && <InitialStockImportDialog onClose={() => setImportOpen(false)} onSuccess={list.reload} />;
  const outboundDialog = outboundOpen && <InventoryOutboundDialog warehouses={warehouses} canLinkException={can('exceptions.read') && can('exceptions.resolve')} onClose={() => setOutboundOpen(false)} onSuccess={list.reload} />;
  const actionAllowed = { inbound: canInbound, outbound: canOutbound, adjust: canAdjust, import: canImport };
  const actionsSheet = actionsOpen && <InventoryActionsSheet
    warehouseName={warehouses.find((item) => item.id === warehouseId)?.name || warehouses.find((item) => item.isDefaultInbound)?.name || warehouses[0]?.name}
    allowed={actionAllowed}
    onClose={() => setActionsOpen(false)}
    onSelect={(action) => {
      setActionsOpen(false);
      if (action === 'inbound') setInboundOpen(true);
      if (action === 'outbound') setOutboundOpen(true);
      if (action === 'adjust') setAdjustOpen(true);
      if (action === 'import') setImportOpen(true);
    }}
  />;
  const canTx = can('inventory.transactions.read');
  const filters = { category, warehouseId };
  const activeFilters = FILTER_KEYS.filter((key) => filters[key]);

  const inventoryChips: ChipItem[] = stockViews.map((v) => ({
      key: v.key,
      label: v.label,
      count: stats ? stats[v.key] : undefined,
      active: view === v.key,
      ...(v.key === 'shortage' ? { to: '/inventory/shortage' } : { onClick: () => set({ view: v.key === 'all' ? undefined : v.key, page: undefined }) }),
    }));
  const chips: ChipItem[] = phone ? [
    ...inventoryChips.filter((item) => item.key === 'all').map((item) => ({ ...item, label: '库存总览' })),
    ...inventoryChips.filter((item) => item.key === 'shortage'),
    ...(canTx ? [{ key: 'tx', label: '出入库流水', to: '/inventory/transactions' }] : []),
    ...(can('purchase.read') ? [{ key: 'transit', label: '在途待入库', to: '/inventory/transit' }] : []),
    ...inventoryChips.filter((item) => item.key === 'noStock'),
    ...inventoryChips.filter((item) => !['all', 'shortage', 'noStock'].includes(item.key)),
    { key: 'warehouse', label: '按仓库明细', to: '/inventory/warehouses' },
    { key: 'change', label: '变动汇总', to: '/inventory/changes' },
    ...(can('imports.read') ? [{ key: 'imports', label: '导入历史', to: '/imports' }] : []),
  ] : [
    { key: 'warehouse', label: '按仓库明细', to: '/inventory/warehouses' },
    { key: 'change', label: '变动汇总', to: '/inventory/changes' },
    ...(can('purchase.read') ? [{ key: 'transit', label: '在途待入库', to: '/inventory/transit' }] : []),
    ...inventoryChips,
    ...(canTx ? [{ key: 'tx', label: '出入库流水', to: '/inventory/transactions' }] : []),
  ];

  const applied = (
    <Applied
      items={activeFilters.map((key) => ({
        key,
        label: key === 'warehouseId' ? warehouses.find((w) => w.id === warehouseId)?.name || '指定仓库' : category || '',
        onRemove: () => set({ [key]: undefined, page: undefined }),
      }))}
      onClear={() => set({ category: undefined, warehouseId: undefined, page: undefined })}
    />
  );

  const search = (cls?: string) => (
    <SearchBox className={cls} value={q} autoFocus={phone && searching && !q} placeholder="产品编号 / 名称 / 分类 / 仓库" onSearch={(v) => set({ q: v || undefined, page: undefined })} />
  );

  const panelEl = panel && (
    <FilterSheet
      sheet={phone}
      value={filters}
      onClose={() => setPanel(false)}
      onApply={(f) => {
        setPanel(false);
        set({ category: f.category, warehouseId: f.warehouseId, page: undefined });
      }}
      groups={[
        { key: 'warehouseId', title: '仓库', opts: warehouses.map((w) => ({ value: w.id, label: w.name })) },
        { key: 'category', title: '分类', opts: categories.map((c) => ({ value: c.category, label: c.category, count: c.productCount })) },
      ]}
      note="库存状态在上方切换；列表按最近变动排在前面。"
    />
  );

  const open = (r: StockSummaryRow) => set({ pid: r.productId });
  const sortColumn = (key: string) => set({ sort: key, dir: sort === key && dir === 'desc' ? 'asc' : 'desc', page: undefined });
  const sortable = (key: string) => ({ onSort: () => sortColumn(key), sortDirection: sort === key ? dir === 'asc' ? 'ascending' as const : 'descending' as const : undefined });
  const sheetEl = pid && (
    <ProductStockSheet productId={pid} row={list.items.find((r) => r.productId === pid)} sheet={phone} onClose={() => set({ pid: undefined })} />
  );

  const body = (() => {
    if (list.error && !list.items.length) {
      return <ErrorState title="库存加载失败" message={list.error.message} detail="GET /api/v2/inventory/summary" onRetry={list.reload} />;
    }
    if (list.loading && !list.items.length) return <div className={k.pad}><Skeleton lines={3} /><Skeleton lines={3} /><Skeleton lines={3} /></div>;
    if (!list.items.length) {
      const filtered = q || activeFilters.length;
      return (
        <Empty icon="box" title={filtered ? '没有符合条件的产品' : `「${stockViews.find((v) => v.key === view)?.label}」里没有产品`}>
          {filtered ? '换个关键词，或者清空筛选再看看。' : '切到「全部库存」看看。'}
        </Empty>
      );
    }
    return null;
  })();

  /* ---------- 表格列：桌面 / iPad 照老系统汇总表；手机只留能横比的几列（L1b） ---------- */
  const wide: Col<StockSummaryRow>[] = [
    { key: 'code', title: '产品编号', w: 136, fix: true, render: (r) => <b className={`${k.productCode} tnum`}>{r.productCode}</b> },
    { key: 'name', title: '产品名称', w: 200, render: (r) => r.productName },
    { key: 'cat', title: '分类', w: 110, render: (r) => r.category || <span className="em">未分类</span> },
    { key: 'wh', title: '仓库分布', w: 190, render: (r) => r.warehouseDistribution?.length ? r.warehouseDistribution.map((warehouse) => `${warehouse.name} ${qty(warehouse.quantity)}`).join(' · ') : warehouseNames(r.warehouses) || <span className="em">—</span> },
    { key: 'cur', title: '现有', w: 90, align: 'r', render: (r) => qty(r.currentQuantity) },
    { key: 'res', title: '预占', w: 90, align: 'r', render: (r) => qty(r.reservedQuantity) },
    { key: 'av', title: '可用', w: 96, align: 'r', render: (r) => <Avail value={r.availableQuantity} strong /> },
    ...(showMoney
      ? [
          { key: 'cost', title: '入库均价', w: 110, align: 'r', render: (r) => (r.inventoryUnitCost !== undefined ? money(r.inventoryUnitCost) : '—') },
          { key: 'value', title: '库存价值', w: 130, align: 'r', render: (r) => (r.inventoryValue !== undefined ? money(r.inventoryValue) : '—') },
        ] as Col<StockSummaryRow>[]
      : []),
  ];
  const tabletWide = ff === 'tablet' ? wide.map((col) => ({ ...col, w: col.w ? Math.round(col.w * .94) : undefined })) : wide;
  const narrow: Col<StockSummaryRow>[] = [
    { key: 'p', title: '产品', w: phone ? 130 : 150, fix: true, render: (r) => <div className={k.cell2}><span>{r.productName}</span><small className="tnum">{r.productCode}</small></div>, ...sortable('productName') },
    { key: 'cur', title: '现有', w: phone ? 58 : 64, align: 'r', render: (r) => qty(r.currentQuantity), ...sortable('currentQuantity') },
    { key: 'res', title: '预占', w: phone ? 58 : 64, align: 'r', render: (r) => qty(r.reservedQuantity), ...sortable('reservedQuantity') },
    { key: 'av', title: '可用', w: phone ? 70 : 76, align: 'r', render: (r) => <Avail value={r.availableQuantity} strong />, ...sortable('availableQuantity') },
    ...(showMoney
      ? [
          { key: 'cost', title: '入库均价', w: 96, align: 'r', render: (r) => (r.inventoryUnitCost !== undefined ? money(r.inventoryUnitCost) : '—'), ...sortable('inventoryUnitCost') },
          { key: 'value', title: '价值', w: 112, align: 'r', render: (r) => (r.inventoryValue !== undefined ? money(r.inventoryValue) : '—'), ...sortable('inventoryValue') },
        ] as Col<StockSummaryRow>[]
      : []),
    { key: 'wh', title: '仓库', w: 180, render: (r) => <span className="em">{r.warehouseDistribution?.length ? r.warehouseDistribution.map((warehouse) => `${warehouse.name} ${qty(warehouse.quantity)}`).join(' · ') : warehouseNames(r.warehouses) || '—'}</span> },
    { key: 'cat', title: '分类', w: 120, render: (r) => r.category || <span className="em">未分类</span> },
  ];

  const viewOptions = [
    { key: 'all', label: '库存总览', icon: 'box', count: stats?.all },
    { key: 'warehouse', label: '按仓库明细', icon: 'bank', to: '/inventory/warehouses' },
    { key: 'change', label: '变动汇总', icon: 'chart', to: '/inventory/changes' },
    { key: 'shortage', label: '负可用', icon: 'alert', hot: true, separator: true, to: '/inventory/shortage' },
    { key: 'transit', label: '在途待入库', icon: 'inbox', ...(can('purchase.read') ? { to: '/inventory/transit' } : { disabledReason: '当前账号没有采购单查看权限' }) },
    { key: 'noStock', label: '零库存', icon: 'flag', count: stats?.noStock },
    ...(canTx ? [{ key: 'tx', label: '出入库流水', icon: 'book', to: '/inventory/transactions', separator: true }] : []),
    ...(can('imports.read') ? [{ key: 'imports', label: '导入历史', icon: 'down', to: '/imports' }] : []),
  ];

  if (ff === 'desktop') {
    return <div className={`${k.desk} ${k.desktopPage}`}>
      <header className={k.desktopHead}>
        <div><h1>库存总览</h1><p>按产品汇总现有、预占、可用三个量<span className="tnum">共 {list.total} 个产品</span></p></div>
        <div className={k.desktopActions}>{canImport && <button type="button" className={k.linkBtn} onClick={() => setImportOpen(true)}>导入期初库存</button>}{canAdjust && <button type="button" className={k.linkBtn} onClick={() => setAdjustOpen(true)}>库存调整</button>}{canOutbound && <button type="button" className={k.linkBtn} onClick={() => setOutboundOpen(true)}>报损出库</button>}{canInbound && <button type="button" className={`${k.linkBtn} ${k.primaryLink}`} onClick={() => setInboundOpen(true)}>手工入库</button>}</div>
      </header>
      <div className={k.desktopWork}>
        <ViewSidebar title="视图" active={view} onPick={(key) => set({ view: key === 'all' ? undefined : key, page: undefined })} views={viewOptions} />
        <section className={k.desktopArea}>
          <div className={k.desktopToolbar}>
            {search()}
            <select className={k.desktopQuickFilter} aria-label="分类" value={category || ''} onChange={(event) => set({ category: event.target.value || undefined, page: undefined })}>
              <option value="">分类：全部</option>{categories.map((item) => <option key={item.category} value={item.category}>{item.category}</option>)}
            </select>
            <select className={k.desktopQuickFilter} aria-label="仓库" value={warehouseId || ''} onChange={(event) => set({ warehouseId: event.target.value || undefined, page: undefined })}>
              <option value="">仓库：全部</option>{warehouses.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
          </div>
          {applied}
          {body ? <div className={k.dbody}>{body}</div> : <DataTable cols={wide} rows={list.items} rowKey={(r) => r.productId} onRowClick={open} selectedKey={pid} />}
          {!body && <Pager page={list.page} pageSize={pageSize} total={list.total} shown={list.items.length} totalPages={list.totalPages} onGo={(p) => set({ page: p > 1 ? String(p) : undefined })} extra={<span className="em">可用 = 现有 − 预占，允许为负</span>} />}
        </section>
      </div>
      {panelEl}{sheetEl}{adjustDialog}{inboundDialog}{importDialog}{outboundDialog}
    </div>;
  }

  /* ------------------------------ 手机 ------------------------------ */
  if (phone) {
    return (
      <div className={k.phone}>
        <PhoneBar
          title="库存"
          actions={
            <>
              <IconButton name="srch" label="搜索" onClick={() => setSearching((x) => !x)} />
              {(canAdjust || canInbound || canImport || canOutbound) && <IconButton name="plus" label="库存操作" onClick={() => setActionsOpen(true)} />}
            </>
          }
        />
        {(searching || q) && <div className={k.psearch}>{search()}</div>}
        <div className={k.chipbar}>
          <Chips items={chips} inset="phone" />
          <FilterButton count={activeFilters.length} onClick={() => setPanel(true)} />
        </div>
        {applied}
        <div className={k.pbody}>
          <div className={k.listbar}>
            <span className="tnum">
              {list.total} 个产品
              {stats?.inventoryValue !== undefined && ` · 总值 ${money(stats.inventoryValue)}`}
            </span>
            <ViewToggle value={mode} onChange={(m) => {
              setPreferredPhoneMode(m);
              try { localStorage.setItem('v2:inventory:phone-mode', m); } catch { /* 浏览器禁用本地存储时仍保留当前页选择 */ }
              set({ mode: m === 'cards' ? undefined : m });
            }} />
          </div>
          {body ||
            (mode === 'cards' ? (
              [...list.items].sort((a, b) => Number(b.availableQuantity < 0) - Number(a.availableQuantity < 0)).map((r) => <StockCard key={r.productId} row={r} onOpen={() => open(r)} />)
            ) : (
              <>
                <div className={k.pcardTable}>
                  <DataTable dense cols={narrow} rows={list.items} rowKey={(r) => r.productId} onRowClick={open} selectedKey={pid} />
                </div>
                <p className={k.foot}>可用 = 现有 − 预占，允许为负。左右滑动看更多列，点行看分仓库存。</p>
              </>
            ))}
          <Sentinel hasNext={list.hasNext} loading={list.loading} count={list.items.length} onMore={list.loadMore} />
        </div>
        {panelEl}
        {sheetEl}
        {adjustDialog}
        {inboundDialog}
        {importDialog}
        {outboundDialog}
        {actionsSheet}
      </div>
    );
  }

  /* --------------------------- iPad / 桌面 --------------------------- */
  return (
    <div className={k.desk}>
      <header className={`${k.dh} ${k.inventoryHeader}`}>
        {ff === 'tablet'
          ? <div className={k.inventoryTitle}><span>{viewOptions.find((option) => option.key === view)?.label || '库存总览'}</span><small className="tnum">{list.total}</small></div>
          : <InventoryViewPopover title={viewOptions.find((option) => option.key === view)?.label || '库存总览'} count={list.total} views={viewOptions} active={view} onPick={(key) => set({ view: key === 'all' ? undefined : key, page: undefined })} />}
        <span className={k.sp} />
        {search()}
        <FilterButton boxed count={activeFilters.length} onClick={() => setPanel(true)} />
        <div className={k.toolbarActions} aria-label="库存操作，左右滑动查看全部">
          <Button icon="ref" onClick={list.reload}>刷新</Button>
          {canAdjust && <button type="button" className={k.linkBtn} onClick={() => setAdjustOpen(true)}>库存调整</button>}
          {canInbound && <button type="button" className={`${k.linkBtn} ${k.primaryLink}`} onClick={() => setInboundOpen(true)}>手工入库</button>}
          {canImport && <button type="button" className={k.linkBtn} onClick={() => setImportOpen(true)}>导入期初库存</button>}
          {canOutbound && <button type="button" className={k.linkBtn} onClick={() => setOutboundOpen(true)}>报损出库</button>}
        </div>
      </header>
      <div className={k.ipadInfo}>
        <button type="button" onClick={() => setPanel(true)}>分类：{category || '全部'}</button>
        <button type="button" onClick={() => setPanel(true)}>仓库：{warehouses.find((warehouse) => warehouse.id === warehouseId)?.name || '全部'}</button>
        <span className="tnum">{list.total} 个产品 · 可用 = 现有 − 预占，允许为负</span>
      </div>
      {applied}
      {body ? (
        <div className={k.dbody}>{body}</div>
      ) : (
        <DataTable cols={tabletWide} rows={list.items} rowKey={(r) => r.productId} onRowClick={open} selectedKey={pid} />
      )}
      {!body && (
        <Pager
          page={list.page}
          pageSize={pageSize}
          total={list.total}
          shown={list.items.length}
          totalPages={list.totalPages}
          onGo={(p) => set({ page: p > 1 ? String(p) : undefined })}
          extra={<span className="em">可用 = 现有 − 预占，允许为负</span>}
        />
      )}
      {panelEl}
      {sheetEl}
      {adjustDialog}
      {inboundDialog}
      {importDialog}
      {outboundDialog}
    </div>
  );
}
